import { Injectable } from "@angular/core";
import { Bill, Invoice } from "../models/finance";
import { Store } from "@ngxs/store";
import { UserState, ApiService, SignalingService, User } from "@harlowe/core";
import { HubConnection } from "@microsoft/signalr";
import { environment } from "../../environments/environment";
import { TealCompEncounter } from "../models/encounter";
import { MedicalBill } from "../models/medical-bill";
import { Encounters } from "../state/encounters/encounter-actions";
import { TealCompMedicalService } from "./medical.service";
import { NotFoundError, catchError, distinctUntilChanged, filter, lastValueFrom, of } from "rxjs";
import { formatDate } from "@angular/common";
import { MessageService } from "primeng/api";

export abstract class FinanceServiceBase {
    abstract findInvoices(after?: Date, before?: Date, includeComplete?: boolean): Promise<Invoice[]>;
    abstract findBills(after?: Date, before?: Date, includeComplete?: boolean): Promise<Bill[]>;


}

@Injectable({ providedIn: 'root' })
export class FinanceService extends FinanceServiceBase {
    currentUser$ = this._store.select(UserState.currentUser);
    private _financeHub: HubConnection;



    constructor
        (
            private _store: Store,
            private _signaling: SignalingService,
            private _medicalService: TealCompMedicalService,
            private _api: ApiService,
            private _messages: MessageService


        ) {
        super();
        this._financeHub = this._signaling.generateHub(environment.harlowe.tealcomp.baseUri + environment.harlowe.tealcomp.signaling.endpoints.finance);

        this.currentUser$
            .pipe(
                filter((cu): cu is User => cu != null && cu.id != null),
                distinctUntilChanged((a, b) => a.id === b.id)
            )
            .subscribe
            (
                cu => {
                    if (cu?.id != null) {
                        //Try starting the hub, once we're logged in
                        this._signaling.startHub(this._financeHub);
                    }
                    else
                    {
                        this._financeHub.stop().catch(err => console.error('Hub stop failed', err));
                    }
                }
            );
        this._financeHub.on("InvoiceCreatedFor", (invoice: Invoice, forThis: TealCompEncounter) => this.invoiceCreated(invoice, forThis));
        this._financeHub.on("BillCreatedFor", (bill: Bill, forThis: MedicalBill) => this.billCreated(bill, forThis));

    }

    private invoiceCreated(invoice: Invoice, encounter: TealCompEncounter) {
        this._store.dispatch(new Encounters.FinanceServiceEncounterInvoiceCreated({ invoice: invoice, encounterId: encounter.id }));
    }

    private billCreated(bill: Bill, medicalBill: MedicalBill) {
        this._medicalService.getEncountersForMedicalBill(medicalBill.id)
            .then
            (
                encounter => encounter.map(e => this._store.dispatch(new Encounters.FinanceServiceEncounterBillCreated({ bill: bill, encounterId: e })))
            )
    }

    override findInvoices(after?: Date, before?: Date, includeComplete?: boolean): Promise<Invoice[]> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<Invoice[]>(environment.harlowe.tealcomp.baseUri, "/finance/invoices",
                    {
                        ...after && { after: formatDate(after, "MM/dd/yyyy", "en-us") },
                        ...before && { before: formatDate(before, "MM/dd/yyyy", "en-us") },
                        ...includeComplete && { includeInvoiced: includeComplete.toString() }
                    }).pipe
                    (
                        catchError((error: NotFoundError) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'No Invoices Found' }); return of([]) })
                    )

            )
    }
    override findBills(after?: Date, before?: Date, includeComplete?: boolean): Promise<Bill[]> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<Bill[]>(environment.harlowe.tealcomp.baseUri, "/finance/bills",
                    {
                        ...after && { after: formatDate(after, "MM/dd/yyyy", "en-us") },
                        ...before && { before: formatDate(before, "MM/dd/yyyy", "en-us") },
                        ...includeComplete && { includePaid: includeComplete.toString() }
                    }).pipe
                    (
                        catchError((error: NotFoundError) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'No Bills Found' }); return of([]) })
                    )

            )
    }
}